import axios from 'axios'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para agregar token a las peticiones
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('colegio-token')
    if (token) {
      config.headers.token = token
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      const errorMessage = error.response.data?.message || 'Error en la solicitud'
      console.error('API Error:', errorMessage)

      // Si es 401, el token es inválido
      // PERO no redirigir si es el endpoint de login (para mostrar el mensaje de error)
      if (error.response.status === 401 && !error.config.url.includes('/login')) {
        localStorage.removeItem('colegio-token')
        localStorage.removeItem('colegio-user')
        // Usar evento para que React maneje el logout limpiamente
        // (window.location.href causaba loops de redirect)
        window.dispatchEvent(new Event('auth:logout'))
      }
    }
    return Promise.reject(error)
  }
)

// ============ Auth API ============
export const authAPI = {
  login: (email, password) => {
    return apiClient.post('/login', { usuario: email, password })
  },
}

// ============ Stats API ============
export const statsAPI = {
  getStats: () => {
    return apiClient.get('/stats')
  },
}

// ============ Security Providers API ============
export const securityProvidersAPI = {
  // Obtener todos los proveedores (opcionalmente incluir inactivos y pendingCount)
  getAll: (includeInactive = false, includePendingCount = false) => {
    const params = [];
    if (includeInactive) params.push('includeInactive=true');
    if (includePendingCount) params.push('includePendingCount=true');
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return apiClient.get(`/security-providers${queryString}`)
  },
  
  // Obtener un proveedor por ID
  getById: (id) => {
    return apiClient.get(`/security-providers/${id}`)
  },
  
  // Crear un nuevo proveedor
  create: (data) => {
    return apiClient.post('/security-providers', data)
  },
  
  // Actualizar un proveedor existente
  update: (id, data) => {
    return apiClient.put(`/security-providers/${id}`, data)
  },
  
  // Eliminar (soft delete) un proveedor
  delete: (id) => {
    return apiClient.delete(`/security-providers/${id}`)
  },
  
  // Reactivar un proveedor inactivo
  reactivate: (id) => {
    return apiClient.put(`/security-providers/${id}/reactivate`)
  },

  // Obtener configuración propia (Admin only)
  getMyConfig: () => {
    return apiClient.get('/security-providers/my-config')
  },

  // Actualizar configuración propia (Admin only)
  updateMyConfig: (data) => {
    return apiClient.patch('/security-providers/my-config', data)
  },
}

// ============ Security Provider Custom Commands API ============
export const securityProviderCustomCommandsAPI = {
  // Obtener todos los comandos (opcionalmente incluir inactivos y filtros)
  /**
   * @param {boolean} includeInactive
   * @param {number|null|undefined} securityProviderId
   * @param {number|null|undefined} panelModelId
   */
  getAll: (includeInactive = false, securityProviderId = null, panelModelId = null) => {
    const params = []
    if (includeInactive) params.push('includeInactive=true')
    if (securityProviderId) params.push(`securityProviderId=${securityProviderId}`)
    if (panelModelId) params.push(`panelModelId=${panelModelId}`)
    const queryString = params.length > 0 ? `?${params.join('&')}` : ''
    return apiClient.get(`/security-provider-custom-commands${queryString}`)
  },

  // Obtener un comando por ID
  getById: (id) => {
    return apiClient.get(`/security-provider-custom-commands/${id}`)
  },

  // Crear un nuevo comando
  create: (data) => {
    return apiClient.post('/security-provider-custom-commands', data)
  },

  // Actualizar un comando existente
  update: (id, data) => {
    return apiClient.put(`/security-provider-custom-commands/${id}`, data)
  },

  // Eliminar (soft delete) un comando
  delete: (id) => {
    return apiClient.delete(`/security-provider-custom-commands/${id}`)
  },

  // Reactivar un comando inactivo
  reactivate: (id) => {
    return apiClient.put(`/security-provider-custom-commands/${id}/reactivate`)
  },
}

// ============ Users API ============
export const usersAPI = {
  // Obtener todos los usuarios (opcionalmente incluir inactivos)
  getAll: (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return apiClient.get(`/users${params}`)
  },
  
  // Obtener usuarios del grupo (para usuarios dueños)
  getMyGroup: (includeInactive = false) => {
    return apiClient.get('/users/my-group', {
      params: { includeInactive }
    })
  },
  
  // Obtener un usuario por ID
  getById: (id) => {
    return apiClient.get(`/users/${id}`)
  },
  
  // Crear un nuevo usuario
  create: (data) => {
    return apiClient.post('/users', data)
  },
  
  // Actualizar un usuario existente
  update: (id, data) => {
    return apiClient.put(`/users/${id}`, data)
  },
  
  // Eliminar (soft delete) un usuario
  delete: (id) => {
    return apiClient.delete(`/users/${id}`)
  },
  
  // Reactivar un usuario inactivo
  reactivate: (id) => {
    return apiClient.put(`/users/${id}/reactivate`)
  },
  
  // Resetear un usuario (status='pending_start', telegram_id=NULL)
  reset: (id) => {
    return apiClient.put(`/users/${id}/reset`)
  },
  
  // Enviar correo de invitación a un usuario
  sendInvitation: (id) => {
    return apiClient.post(`/users/${id}/send-invitation`)
  },
  
  // Previsualizar contraseña que se generaría al resetear un usuario
  previewResetPassword: (id) => {
    return apiClient.get(`/users/${id}/preview-reset-password`)
  },
  
  // Deshabilitar usuario del grupo (owner only)
  disableGroupUser: (userId) => {
    return apiClient.delete(`/users/my-group/${userId}`)
  },
  
  // Reactivar usuario del grupo (owner only)
  reactivateGroupUser: (userId) => {
    return apiClient.put(`/users/my-group/${userId}/reactivate`)
  },
  
  // Transferir titularidad de grupo de un owner a otro usuario
  transferOwnership: (currentOwnerId, newOwnerId) => {
    return apiClient.post(`/users/${currentOwnerId}/transfer-ownership`, { new_owner_id: newOwnerId })
  },
}

// ============ Panels API ============
export const panelsAPI = {
  // Obtener todos los paneles (opcionalmente incluir inactivos)
  getAll: (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return apiClient.get(`/panels${params}`)
  },
  
  // Obtener un panel por ID
  getById: (id) => {
    return apiClient.get(`/panels/${id}`)
  },
  
  // Crear un nuevo panel
  create: (data) => {
    return apiClient.post('/panels', data)
  },
  
  // Actualizar un panel existente
  update: (id, data) => {
    return apiClient.put(`/panels/${id}`, data)
  },

  // Actualizar solo el nombre personalizado de un panel (para usuarios regulares)
  updateCustomName: (id, customName) => {
    return apiClient.patch(`/panels/${id}/custom-name`, { custom_name: customName })
  },
  
  // Eliminar (soft delete) un panel
  delete: (id) => {
    return apiClient.delete(`/panels/${id}`)
  },
  
  // Reactivar un panel inactivo
  reactivate: (id) => {
    return apiClient.put(`/panels/${id}/reactivate`)
  },

  // Obtener usuarios asignados a un panel
  getUsers: (panelId) => {
    return apiClient.get(`/panels/${panelId}/users`)
  },

  // Obtener usuarios disponibles para asignar a un panel
  getAvailableUsers: (panelId) => {
    return apiClient.get(`/panels/${panelId}/available-users`)
  },

  // Asignar usuario a panel
  assignUser: (panelId, userId) => {
    return apiClient.post(`/panels/${panelId}/users`, { userId })
  },

  // Remover usuario de panel
  removeUser: (panelId, userId) => {
    return apiClient.delete(`/panels/${panelId}/users/${userId}`)
  },

  // Obtener eventos del panel
  getEvents: (panelId, page = 1, pageSize = 10) => {
    return apiClient.get(`/panels/${panelId}/events?page=${page}&pageSize=${pageSize}`)
  },

  // Obtener último evento del panel
  getLatestEvent: (panelId) => {
    return apiClient.get(`/panels/${panelId}/latest-event`)
  },

  // Enviar comando a panel (para admins con technical_mode)
  sendCommand: (panelId, command) => {
    return apiClient.post(`/panels/${panelId}/send-command`, { command })
  },

  // Enviar comando de bot a mi panel (para usuarios dueños)
  sendBotCommand: (panelId, botCommandId, secret = null) => {
    const payload = { bot_command_id: botCommandId }
    if (secret !== null) {
      payload.secret = secret
    }
    return apiClient.post(`/panels/${panelId}/send-bot-command`, payload)
  },

  // Obtener configuración de notificaciones de un panel
  // Si se pasa userId, obtiene configuraciones de ese usuario (requiere ser owner)
  getNotificationSettings: (panelId, userId = null) => {
    const url = userId
      ? `/my-panels/${panelId}/notification-settings?user_id=${userId}`
      : `/my-panels/${panelId}/notification-settings`
    return apiClient.get(url)
  },

  // Activar/desactivar categoría de notificación
  // Si se pasa userId, cambia configuraciones de ese usuario (requiere ser owner)
  toggleCategoryNotification: (panelId, categoryId, userId) => {
    const body = userId !== null && userId !== undefined
      ? {category_id: categoryId, user_id: userId}
      : {category_id: categoryId}
    return apiClient.post(`/my-panels/${panelId}/notification-settings/toggle`, body)
  },

  // Obtener estadísticas de actividad de paneles
  getActivityStats: () => {
    return apiClient.get('/panels/stats/activity')
  },

  // Obtener paneles asignados a un usuario
  getPanelsByUserId: (userId) => {
    return apiClient.get(`/users/${userId}/panels`)
  },
}

// ============ User-Panels API ============
export const userPanelsAPI = {
  // Obtener paneles asignados a un usuario
  getPanelsByUserId: (userId) => {
    return apiClient.get(`/users/${userId}/panels`)
  },

  // Asignar panel a usuario
  assignPanel: (userId, panelId) => {
    return apiClient.post(`/users/${userId}/panels`, { panelId })
  },

  // Remover panel de usuario
  removePanel: (userId, panelId) => {
    return apiClient.delete(`/users/${userId}/panels/${panelId}`)
  },
}

// ============ My Group User-Panels API (Owner users only) ============
export const myGroupPanelsAPI = {
  // Obtener paneles asignados a un usuario de mi grupo
  getPanelsByUserId: (userId) => {
    return apiClient.get(`/users/my-group/${userId}/panels`)
  },

  // Asignar panel a usuario de mi grupo
  assignPanel: (userId, panelId) => {
    return apiClient.post(`/users/my-group/${userId}/panels`, { panelId })
  },

  // Remover panel de usuario de mi grupo
  removePanel: (userId, panelId) => {
    return apiClient.delete(`/users/my-group/${userId}/panels/${panelId}`)
  },
}

// ============ Bot Commands API ============
export const botCommandsAPI = {
  // Obtener comandos visibles para web
  getWebVisibleCommands: () => {
    return apiClient.get('/bot-commands')
  },
}

// ============ Panel Notification Categories API ============
export const panelNotificationCategoriesAPI = {
  // Obtener todas las categorías
  getAll: () => {
    return apiClient.get('/panel-notification-categories')
  },
  // Obtener por ID
  getById: (id) => {
    return apiClient.get(`/panel-notification-categories/${id}`)
  },
  // Crear categoría
  create: (data) => {
    return apiClient.post('/panel-notification-categories', data)
  },
  // Actualizar categoría
  update: (id, data) => {
    return apiClient.put(`/panel-notification-categories/${id}`, data)
  },
  // Eliminar categoría
  delete: (id) => {
    return apiClient.delete(`/panel-notification-categories/${id}`)
  },
}

// ============ Panel Notification Types API ============
export const panelNotificationTypesAPI = {
  // Obtener todos los tipos
  getAll: (includeInactive = false) => {
    return apiClient.get('/panel-notification-types', {
      params: { includeInactive: includeInactive.toString() }
    })
  },
  // Obtener por ID
  getById: (id) => {
    return apiClient.get(`/panel-notification-types/${id}`)
  },
  // Crear nuevo tipo
  create: (data) => {
    return apiClient.post('/panel-notification-types', data)
  },
  // Actualizar tipo
  update: (id, data) => {
    return apiClient.put(`/panel-notification-types/${id}`, data)
  },
  // Deshabilitar tipo
  softDelete: (id) => {
    return apiClient.delete(`/panel-notification-types/${id}`)
  },
  // Reactivar tipo
  reactivate: (id) => {
    return apiClient.put(`/panel-notification-types/${id}/reactivate`)
  },
}

// ============ Panel Models API ============
export const panelModelsAPI = {
  // Obtener todos los modelos
  getAll: (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return apiClient.get(`/panel-models${params}`)
  },
  // Obtener modelo por ID
  getById: (id) => {
    return apiClient.get(`/panel-models/${id}`)
  },
  // Crear modelo (SA only)
  create: (data) => {
    return apiClient.post('/panel-models', data)
  },
  // Actualizar modelo (SA only)
  update: (id, data) => {
    return apiClient.put(`/panel-models/${id}`, data)
  },
  // Deshabilitar modelo (SA only)
  delete: (id) => {
    return apiClient.delete(`/panel-models/${id}`)
  },
  // Reactivar modelo (SA only)
  reactivate: (id) => {
    return apiClient.put(`/panel-models/${id}/reactivate`)
  },
}

// ============ Panel Events API ============
export const panelEventsAPI = {
  // Obtener eventos desconocidos con filtros
  getUnknownEvents: (params) => {
    return apiClient.get('/panel-events/unknown', { params })
  },
  // Obtener conteo de retransmisiones pendientes
  getPendingRetransmissionsCount: () => {
    return apiClient.get('/panel-event-retransmissions/pending-count')
  },
  // Descartar todas las retransmisiones pendientes
  discardPendingRetransmissions: () => {
    return apiClient.patch('/panel-event-retransmissions/discard-pending')
  },
}

// ============ Panel Event Retransmissions API ============
export const panelEventRetransmissionsAPI = {
  // Descartar retransmisiones pendientes de un proveedor específico
  discardPending: (securityProviderId) => {
    return apiClient.delete(`/panel-event-retransmissions/discard-pending/${securityProviderId}`)
  },
}

export default apiClient
